import { FC, useMemo } from 'react';
import { Box, Typography, Divider, Grid } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { CategoryCard } from 'widgets/startRecognition/ui/CategoryCard';
import { CategoriesList, Category } from 'widgets/startRecognition/consts/CategoryList';
import { ColorPalette } from 'shared/consts/colorPalette';
import { useCustomModelStore } from 'widgets/customModel/state';
import { CategoryTypeEnum } from 'entities/Files/types';

interface Props {
	openUploadModal: (category: Category) => void;
}

export const CategoryList: FC<Props> = ({ openUploadModal }) => {
	const { t } = useTranslation();
	const { customModels, generativeModels } = useCustomModelStore();

	const customModelCategory = useMemo(
		() => CategoriesList.find((item) => item.id === CategoryTypeEnum.custom),
		[],
	);

	const generativeModelCategory = useMemo(
		() => CategoriesList.find((item) => item.id === CategoryTypeEnum.generative_model),
		[],
	);

	const standardModelsLits = useMemo(
		() =>
			CategoriesList.filter(
				(item) =>
					item.id !== CategoryTypeEnum.generative_model && item.id !== CategoryTypeEnum.custom,
			),
		[],
	);

	return (
		<>
			{customModels.length ? (
				<>
					<Box>
						<Typography variant={'h5'} color={ColorPalette.astronaut} sx={{ marginBottom: 3 }}>
							{t('sidebar.custom_models')}
						</Typography>
						<Grid container spacing={2.5}>
							{customModelCategory &&
								customModels?.map((model) => (
									<Grid size={4} key={model._id}>
										<CategoryCard
											category={{
												...customModelCategory,
												label: model.name,
											}}
											action={() =>
												openUploadModal({
													...customModelCategory,
													customModelId: model._id,
												})
											}
											sx={{ height: '100%' }}
										/>
									</Grid>
								))}
						</Grid>
					</Box>

					<Divider color={ColorPalette.periwinkle} sx={{ margin: '20px 0' }} />
				</>
			) : null}

			{generativeModels.length ? (
				<>
					<Box>
						<Typography variant={'h5'} color={ColorPalette.astronaut} sx={{ marginBottom: 3 }}>
							{t('sidebar.generative_models')}
						</Typography>
						<Grid container spacing={2.5}>
							{generativeModelCategory &&
								generativeModels?.map((model) => (
									<Grid size={4} key={model._id}>
										<CategoryCard
											category={{
												...generativeModelCategory,
												label: model.name,
											}}
											action={() =>
												openUploadModal({
													...generativeModelCategory,
													generativeId: model._id,
												})
											}
											sx={{ height: '100%' }}
										/>
									</Grid>
								))}
						</Grid>
					</Box>
					<Divider color={ColorPalette.periwinkle} sx={{ margin: '20px 0' }} />
				</>
			) : null}

			<Box>
				<Typography variant={'h5'} color={ColorPalette.astronaut} sx={{ marginBottom: 3 }}>
					{t('sidebar.standard_models')}
				</Typography>
			</Box>
			<Grid container spacing={2.5}>
				{standardModelsLits.map((category) => (
					<Grid size={4} key={category.id}>
						<CategoryCard
							category={category}
							action={() => openUploadModal(category)}
							sx={{ height: '100%' }}
						/>
					</Grid>
				))}
			</Grid>
		</>
	);
};
